import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { bookingService } from '../services/bookingService';
import { calendarService } from '../services/calendarService';
import { emailService } from '../services/emailService';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiCalendar, FiClock, FiUser, FiMail, FiDownload, FiHome } = FiIcons;

const BookingConfirmationPage = () => {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [slot, setSlot] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBookingDetails();
  }, [bookingId]);

  const loadBookingDetails = async () => {
    try {
      setLoading(true);
      const bookingData = await bookingService.getBookingById(bookingId);
      setBooking(bookingData);

      // Load calendar details
      const calendarData = await calendarService.getCalendarByShareId(bookingData.shareId);
      setCalendar(calendarData);

      // Find the booked slot
      const bookedSlot = calendarData.slots.find(s => s.id === bookingData.slotId);
      setSlot(bookedSlot);
    } catch (error) {
      console.error('Error loading booking details:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadICS = () => {
    if (!booking || !slot || !calendar) return;

    const icsContent = emailService.generateICSFile(booking, slot, calendar);
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `termin-${booking.id}.ics`;
    link.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!booking || !calendar || !slot) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Buchung nicht gefunden
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Die angeforderte Buchung existiert nicht oder ist nicht verfügbar.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-20 h-20 bg-success-100 dark:bg-success-900 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <SafeIcon icon={FiCheck} className="w-10 h-10 text-success-600 dark:text-success-400" />
          </motion.div>

          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Buchung bestätigt!
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Ihr Termin wurde erfolgreich gebucht
          </p>
        </motion.div>

        {/* Booking Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="p-8 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
              Termindetails
            </h2>

            <div className="space-y-4">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center mr-4">
                  <SafeIcon icon={FiCalendar} className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Termin</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {calendar.title}
                  </p>
                </div>
              </div>

              <div className="flex items-center">
                <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center mr-4">
                  <SafeIcon icon={FiCalendar} className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Datum</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {format(parseISO(slot.date), 'EEEE, dd. MMMM yyyy', { locale: de })}
                  </p>
                </div>
              </div>

              <div className="flex items-center">
                <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center mr-4">
                  <SafeIcon icon={FiClock} className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Uhrzeit</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {slot.time} ({slot.duration} Minuten)
                  </p>
                </div>
              </div>

              <div className="flex items-center">
                <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center mr-4">
                  <SafeIcon icon={FiUser} className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Name</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {booking.booker.name}
                  </p>
                </div>
              </div>

              <div className="flex items-center">
                <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center mr-4">
                  <SafeIcon icon={FiMail} className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">E-Mail</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {booking.booker.email}
                  </p>
                </div>
              </div>

              {booking.booker.notes && (
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Nachricht</p>
                  <p className="text-gray-900 dark:text-white">
                    {booking.booker.notes}
                  </p>
                </div>
              )}
            </div>

            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Buchungs-ID:</strong> {booking.id}
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                Eine Bestätigungs-E-Mail wurde an {booking.booker.email} gesendet.
              </p>
            </div>
          </Card>
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <Button onClick={downloadICS} variant="outline">
            <SafeIcon icon={FiDownload} className="w-4 h-4 mr-2" />
            Kalender-Datei herunterladen
          </Button>
          <Link to="/">
            <Button>
              <SafeIcon icon={FiHome} className="w-4 h-4 mr-2" />
              Zur Startseite
            </Button>
          </Link>
        </motion.div>

        {/* Status Info */}
        {booking.status === 'pending' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-6"
          >
            <Card className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800">
              <p className="text-yellow-800 dark:text-yellow-200 text-sm">
                <strong>Hinweis:</strong> Ihr Termin muss noch vom Kalenderbesitzer bestätigt werden. 
                Sie erhalten eine weitere E-Mail, sobald der Termin bestätigt wurde.
              </p>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default BookingConfirmationPage;